import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Car } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const navLinks = [
  { label: 'Accueil', href: '/' },
  { label: 'Vendre', href: '/vendre-voiture-montelimar' },
  { label: 'Estimer', href: '/estimer-voiture' },
  { label: 'Acheter', href: '/acheter-voiture-montelimar' },
  { label: 'Dépôt-vente', href: '/depot-vente-montelimar' },
  { label: 'Entreprises', href: '/entreprises' },
  { label: 'Blog', href: '/blog' },
  { label: 'Contact', href: '/contact' },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location]);

  return (
    <header
      data-testid="header"
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? 'py-2 bg-[#070707]/80 backdrop-blur-2xl border-b border-white/[0.04] shadow-[0_4px_30px_rgba(0,0,0,0.3)]'
          : 'py-4 bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 group">
          <motion.div
            whileHover={{ scale: 1.05 }}
            transition={{ type: 'spring', stiffness: 400 }}
          >
            <img src="/logo-guideautofrance-80.webp" alt="GuideAutoFrance" className="w-10 h-10 rounded-xl object-cover" width={40} height={40} />
          </motion.div>
          <span className="text-lg font-bold font-['Outfit'] tracking-tight">
            Guide<span className="text-yellow-400">Auto</span>France
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-0.5" data-testid="desktop-nav">
          {navLinks.map(l => (
            <Link
              key={l.href}
              to={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace(/[éè]/g, 'e').replace(/ô/g, 'o').replace('-', '')}`}
              className={`relative px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                location.pathname === l.href
                  ? 'text-yellow-400'
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              {l.label}
              {location.pathname === l.href && (
                <motion.span
                  layoutId="nav-indicator"
                  className="absolute inset-0 bg-yellow-400/[0.08] rounded-full -z-10"
                  transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                />
              )}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/estimer-voiture"
            data-testid="header-cta-estimate"
            className="btn-primary py-2.5 px-6 text-sm neon-glow-hover"
          >
            Estimer mon véhicule
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
          className="lg:hidden text-white p-2 hover:bg-white/5 rounded-xl transition-colors"
          onClick={() => setOpen(!open)}
        >
          <AnimatePresence mode="wait">
            {open ? (
              <motion.div key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
                <X className="w-6 h-6" />
              </motion.div>
            ) : (
              <motion.div key="menu" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }}>
                <Menu className="w-6 h-6" />
              </motion.div>
            )}
          </AnimatePresence>
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
            className="lg:hidden glass mt-2 mx-4 rounded-2xl p-3 overflow-hidden"
          >
            {navLinks.map((l, i) => (
              <motion.div
                key={l.href}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.04 }}
              >
                <Link
                  to={l.href}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[éè]/g, 'e').replace(/ô/g, 'o').replace('-', '')}`}
                  className={`block px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                    location.pathname === l.href
                      ? 'text-yellow-400 bg-yellow-400/[0.08]'
                      : 'text-zinc-400 hover:text-white hover:bg-white/[0.03]'
                  }`}
                >
                  {l.label}
                </Link>
              </motion.div>
            ))}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
              <Link
                to="/estimer-voiture"
                data-testid="mobile-cta-estimate"
                className="block mt-2 btn-primary py-3 px-6 text-sm text-center"
              >
                Estimer mon véhicule
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
